import { useState, useEffect, useCallback } from "react";
import {
  Phone, PhoneCall, PhoneOff, Plus, Users, Copy, Check,
  Loader2, Mic2, X, UserPlus, Radio, Video, ExternalLink, Trash2
} from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const API = BACKEND_URL;

export const CallInTab = ({ currentUser, authToken }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [stationName, setStationName] = useState(currentUser?.station_name || "");
  const [callOutPhone, setCallOutPhone] = useState("");
  const [callOutRoom, setCallOutRoom] = useState(null);
  const [callingOut, setCallingOut] = useState(false);
  const [hostPhone, setHostPhone] = useState("");
  const [joiningRoom, setJoiningRoom] = useState(null);
  const [joiningHost, setJoiningHost] = useState(false);
  const [copied, setCopied] = useState(null);
  const [error, setError] = useState("");

  // Zoom state
  const [zoomMeetings, setZoomMeetings] = useState([]);
  const [zoomTopic, setZoomTopic] = useState("");
  const [creatingZoom, setCreatingZoom] = useState(false);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` };

  const fetchRooms = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/callin/rooms`, { headers });
      if (r.ok) setRooms((await r.json()).rooms || []);
    } catch {}
    setLoading(false);
  }, [authToken]);

  useEffect(() => { fetchRooms(); const id = setInterval(fetchRooms, 10000); return () => clearInterval(id); }, [fetchRooms]);

  const fetchZoom = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/zoom/meetings`, { headers });
      if (r.ok) setZoomMeetings((await r.json()).meetings || []);
    } catch {}
  }, [authToken]);

  useEffect(() => { fetchZoom(); }, [fetchZoom]);

  const createRoom = async () => {
    if (!stationName.trim()) return setError("Enter a station name");
    setCreating(true); setError("");
    try {
      const r = await fetch(`${API}/api/callin/room`, { method: "POST", headers, body: JSON.stringify({ station_name: stationName }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      setError("");
      await fetchRooms();
    } catch (e) { setError(e.message); }
    setCreating(false);
  };

  const closeRoom = async (roomId) => {
    await fetch(`${API}/api/callin/room/${roomId}/close`, { method: "PUT", headers });
    await fetchRooms();
  };

  const callGuest = async (roomId) => {
    if (!callOutPhone.trim()) return;
    setCallingOut(true); setError("");
    try {
      const r = await fetch(`${API}/api/callin/room/${roomId}/call-out?phone=${encodeURIComponent(callOutPhone)}`, { method: "POST", headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Call failed");
      setCallOutPhone(""); setCallOutRoom(null);
      await fetchRooms();
    } catch (e) { setError(e.message); }
    setCallingOut(false);
  };

  const joinAsHost = async (roomId) => {
    if (!hostPhone.trim()) return;
    setJoiningHost(true); setError("");
    try {
      const r = await fetch(`${API}/api/callin/room/${roomId}/join-host?phone=${encodeURIComponent(hostPhone)}`, { method: "POST", headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed to join");
      setJoiningRoom(null); setHostPhone("");
      await fetchRooms();
    } catch (e) { setError(e.message); }
    setJoiningHost(false);
  };

  const createZoom = async () => {
    if (!zoomTopic.trim()) return setError("Enter a topic");
    setCreatingZoom(true); setError("");
    try {
      const r = await fetch(`${API}/api/zoom/create`, { method: "POST", headers, body: JSON.stringify({ topic: zoomTopic, station_name: stationName }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      setZoomTopic(""); setError("");
      await fetchZoom();
    } catch (e) { setError(e.message); }
    setCreatingZoom(false);
  };

  const endZoom = async (meetingId) => {
    await fetch(`${API}/api/zoom/meetings/${meetingId}/end`, { method: "PUT", headers });
    await fetchZoom();
  };

  const deleteZoom = async (meetingId) => {
    await fetch(`${API}/api/zoom/meetings/${meetingId}`, { method: "DELETE", headers });
    await fetchZoom();
  };

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const activeRooms = rooms.filter(r => r.status === "open" || r.status === "live");

  return (
    <div className="space-y-6" data-testid="callin-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-purple-400" /> Call-In Studio
          </h2>
          <p className="text-white/40 text-sm mt-1">Manage live call-in rooms for interviews and listener interaction</p>
        </div>
      </div>

      {/* Create Room */}
      <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5" data-testid="create-room-section">
        <h3 className="text-white font-semibold text-sm mb-3">Start a Call-In Room</h3>
        <div className="flex gap-3">
          <input type="text" value={stationName} onChange={(e) => setStationName(e.target.value)}
            placeholder="Station or show name..."
            className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-lg py-2.5 px-4 text-white placeholder-white/20 text-sm focus:outline-none focus:border-purple-500/50"
            data-testid="room-station-name" />
          <button onClick={createRoom} disabled={creating}
            className="bg-purple-500 hover:bg-purple-600 text-white font-semibold px-5 py-2.5 rounded-lg transition text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="create-room-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create Room
          </button>
        </div>
        {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
      </div>

      {/* Active Rooms */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-purple-400 animate-spin" /></div>
      ) : activeRooms.length === 0 ? (
        <div className="text-center py-12 text-white/30">
          <Phone className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>No active call-in rooms</p>
          <p className="text-xs mt-1">Create a room above to start accepting callers</p>
        </div>
      ) : (
        <div className="space-y-4">
          {activeRooms.map((room) => (
            <div key={room.room_id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden" data-testid={`room-${room.room_id}`}>
              {/* Room Header */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${room.status === "live" ? "bg-red-500/20" : "bg-purple-500/20"}`}>
                    {room.status === "live" ? <Mic2 className="w-5 h-5 text-red-400" /> : <Phone className="w-5 h-5 text-purple-400" />}
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                      {room.room_name}
                      {room.status === "live" && (
                        <span className="flex items-center gap-1 bg-red-500/20 text-red-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" /> LIVE
                        </span>
                      )}
                      {room.status === "open" && (
                        <span className="bg-green-500/20 text-green-400 text-[10px] font-bold px-2 py-0.5 rounded-full">OPEN</span>
                      )}
                    </h4>
                    <p className="text-white/30 text-xs">{room.station_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {room.status !== "live" && !joiningRoom && (
                    <button onClick={() => setJoiningRoom(room.room_id)}
                      className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white px-4 py-1.5 rounded-lg text-xs font-semibold transition"
                      data-testid={`go-live-${room.room_id}`}>
                      <Mic2 className="w-3.5 h-3.5" /> Go Live
                    </button>
                  )}
                  {room.status === "live" && (
                    <span className="flex items-center gap-1.5 bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg text-xs font-semibold">
                      <Mic2 className="w-3.5 h-3.5" /> You're On Air
                    </span>
                  )}
                  <button onClick={() => closeRoom(room.room_id)}
                    className="flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium transition"
                    data-testid={`close-room-${room.room_id}`}>
                    <PhoneOff className="w-3.5 h-3.5" /> End Room
                  </button>
                </div>
              </div>

              {/* Room Details */}
              <div className="px-4 pb-4 space-y-3">
                {/* Dial-In Info */}
                <div className="bg-white/[0.02] rounded-lg p-3 space-y-2">
                  <p className="text-white/50 text-xs font-medium uppercase tracking-wider">Dial-In Info</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 text-white/30" />
                      <span className="text-white text-sm font-mono">{room.phone_number}</span>
                    </div>
                    <button onClick={() => copyText(room.phone_number, `phone-${room.room_id}`)}
                      className="text-white/30 hover:text-white/60 transition" data-testid={`copy-phone-${room.room_id}`}>
                      {copied === `phone-${room.room_id}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Radio className="w-3.5 h-3.5 text-white/30" />
                      <span className="text-white/60 text-xs">Room Code: <span className="text-white font-mono">{room.room_id}</span></span>
                    </div>
                    <button onClick={() => copyText(room.room_id, `code-${room.room_id}`)}
                      className="text-white/30 hover:text-white/60 transition" data-testid={`copy-code-${room.room_id}`}>
                      {copied === `code-${room.room_id}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Video className="w-3.5 h-3.5 text-white/30" />
                      <span className="text-white/60 text-xs">Browser Link</span>
                    </div>
                    <button onClick={() => copyText(`${window.location.origin}/callin?room=${room.room_id}`, `link-${room.room_id}`)}
                      className="flex items-center gap-1.5 text-purple-400 hover:text-purple-300 text-xs font-medium transition"
                      data-testid={`copy-link-${room.room_id}`}>
                      {copied === `link-${room.room_id}` ? <><Check className="w-3.5 h-3.5 text-green-400" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy Guest Link</>}
                    </button>
                  </div>
                </div>

                {/* Go Live — enter your phone */}
                {joiningRoom === room.room_id && (
                  <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3">
                    <p className="text-green-400 text-xs font-semibold mb-2">Enter your phone number — we'll call you and connect you to the room</p>
                    <div className="flex gap-2">
                      <input type="tel" value={hostPhone} onChange={(e) => setHostPhone(e.target.value)}
                        placeholder="+1 555 123 4567"
                        className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-lg py-2 px-3 text-white placeholder-white/20 text-sm focus:outline-none focus:border-green-500/50"
                        data-testid={`host-phone-${room.room_id}`} />
                      <button onClick={() => joinAsHost(room.room_id)} disabled={joiningHost}
                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition disabled:opacity-50"
                        data-testid={`host-join-btn-${room.room_id}`}>
                        {joiningHost ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic2 className="w-3.5 h-3.5" />} Go Live
                      </button>
                      <button onClick={() => { setJoiningRoom(null); setHostPhone(""); }}
                        className="text-white/30 hover:text-white/60 p-2 transition">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Callers */}
                {room.callers?.length > 0 && (
                  <div>
                    <p className="text-white/50 text-xs font-medium uppercase tracking-wider mb-2">
                      <Users className="w-3 h-3 inline mr-1" /> Callers ({room.callers.length})
                    </p>
                    <div className="space-y-1">
                      {room.callers.map((c, i) => (
                        <div key={i} className="flex items-center justify-between bg-white/[0.02] rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                              <Phone className="w-3 h-3 text-purple-400" />
                            </div>
                            <span className="text-white/70 text-xs font-mono">{c.phone || "Browser"}</span>
                            <span className="text-white/20 text-[10px]">{c.type}</span>
                          </div>
                          <span className="text-white/20 text-[10px]">{new Date(c.joined_at).toLocaleTimeString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Call Out to Guest */}
                {callOutRoom === room.room_id ? (
                  <div className="flex gap-2">
                    <input type="tel" value={callOutPhone} onChange={(e) => setCallOutPhone(e.target.value)}
                      placeholder="+1 555 123 4567"
                      className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-lg py-2 px-3 text-white placeholder-white/20 text-sm focus:outline-none focus:border-purple-500/50"
                      data-testid={`callout-phone-${room.room_id}`} />
                    <button onClick={() => callGuest(room.room_id)} disabled={callingOut}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition disabled:opacity-50"
                      data-testid={`callout-btn-${room.room_id}`}>
                      {callingOut ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhoneCall className="w-3.5 h-3.5" />} Call
                    </button>
                    <button onClick={() => { setCallOutRoom(null); setCallOutPhone(""); }}
                      className="text-white/30 hover:text-white/60 p-2 transition">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setCallOutRoom(room.room_id)}
                    className="flex items-center gap-1.5 text-purple-400 hover:text-purple-300 text-xs font-medium transition"
                    data-testid={`add-caller-${room.room_id}`}>
                    <UserPlus className="w-3.5 h-3.5" /> Call a Guest
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── ZOOM INTERVIEWS ─── */}
      <div className="border-t border-white/[0.06] pt-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Video className="w-5 h-5 text-blue-400" /> Zoom Interviews
          </h3>
        </div>

        {/* Create Zoom */}
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 mb-4" data-testid="create-zoom-section">
          <h4 className="text-white font-semibold text-sm mb-3">Start Zoom Interview</h4>
          <div className="flex gap-3">
            <input type="text" value={zoomTopic} onChange={(e) => setZoomTopic(e.target.value)}
              placeholder="Interview topic..."
              className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-lg py-2.5 px-4 text-white placeholder-white/20 text-sm focus:outline-none focus:border-blue-500/50"
              data-testid="zoom-topic-input" />
            <button onClick={createZoom} disabled={creatingZoom}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-5 py-2.5 rounded-lg transition text-sm flex items-center gap-2 disabled:opacity-50"
              data-testid="create-zoom-btn">
              {creatingZoom ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
              Start Zoom
            </button>
          </div>
        </div>

        {/* Active Zoom Meetings */}
        {zoomMeetings.filter(m => m.status !== "ended" && m.status !== "deleted").length === 0 ? (
          <div className="text-center py-8 text-white/30">
            <Video className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No active Zoom interviews</p>
          </div>
        ) : (
          <div className="space-y-3">
            {zoomMeetings.filter(m => m.status !== "ended" && m.status !== "deleted").map((meeting) => (
              <div key={meeting.meeting_id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" data-testid={`zoom-${meeting.meeting_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-blue-400" />
                    <span className="text-white font-semibold text-sm">{meeting.topic}</span>
                    {meeting.status === "live" && (
                      <span className="flex items-center gap-1 bg-red-500/20 text-red-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" /> LIVE
                      </span>
                    )}
                    {meeting.status === "scheduled" && (
                      <span className="bg-blue-500/20 text-blue-400 text-[10px] font-bold px-2 py-0.5 rounded-full">READY</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => endZoom(meeting.meeting_id)}
                      className="flex items-center gap-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium transition"
                      data-testid={`end-zoom-${meeting.meeting_id}`}>
                      <PhoneOff className="w-3 h-3" /> End
                    </button>
                    <button onClick={() => deleteZoom(meeting.meeting_id)}
                      className="text-white/20 hover:text-red-400 p-1.5 transition"
                      data-testid={`delete-zoom-${meeting.meeting_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-white/[0.02] rounded-lg px-3 py-2">
                    <span className="text-white/50 text-xs">Host Controls</span>
                    <div className="flex items-center gap-2">
                      <a href={meeting.start_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-semibold transition"
                        data-testid={`open-host-${meeting.meeting_id}`}>
                        <ExternalLink className="w-3 h-3" /> Open Host Controls
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-white/[0.02] rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-white/50 text-xs flex-shrink-0">Guest Link</span>
                      <span className="text-white/70 text-xs font-mono truncate">{meeting.join_url}</span>
                    </div>
                    <button onClick={() => copyText(meeting.join_url, `zoom-${meeting.meeting_id}`)}
                      className="text-white/30 hover:text-white/60 transition ml-2 flex-shrink-0"
                      data-testid={`copy-zoom-${meeting.meeting_id}`}>
                      {copied === `zoom-${meeting.meeting_id}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  {meeting.password && (
                    <div className="flex items-center justify-between bg-white/[0.02] rounded-lg px-3 py-2">
                      <span className="text-white/50 text-xs">Password</span>
                      <span className="text-white/70 text-xs font-mono">{meeting.password}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Past Interviews */}
        {zoomMeetings.filter(m => m.status === "ended").length > 0 && (
          <div className="mt-4">
            <p className="text-white/30 text-xs font-medium uppercase tracking-wider mb-2">Past Interviews</p>
            <div className="space-y-1">
              {zoomMeetings.filter(m => m.status === "ended").map((m) => (
                <div key={m.meeting_id} className="flex items-center justify-between bg-white/[0.02] rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Video className="w-3 h-3 text-white/20" />
                    <span className="text-white/40 text-xs">{m.topic}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white/20 text-[10px]">{new Date(m.created_at).toLocaleDateString()}</span>
                    <button onClick={() => deleteZoom(m.meeting_id)} className="text-white/15 hover:text-red-400 transition">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
